import { z } from "https://esm.sh/zod@3.23.8";

const CareerForm = {
  svgSuccess:
    '<svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 256 256" aria-hidden="true" focusable="false"><path d="M173.66,98.34a8,8,0,0,1,0,11.32l-56,56a8,8,0,0,1-11.32,0l-24-24a8,8,0,0,1,11.32-11.32L112,148.69l50.34-50.35A8,8,0,0,1,173.66,98.34ZM232,128A104,104,0,1,1,128,24,104.11,104.11,0,0,1,232,128Zm-16,0a88,88,0,1,0-88,88A88.1,88.1,0,0,0,216,128Z"></path></svg>',

  svgError:
    '<svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 256 256" aria-hidden="true" focusable="false"><path d="M236.8,188.09,149.35,36.22a24.76,24.76,0,0,0-42.7,0L19.2,188.09a23.51,23.51,0,0,0,0,23.72A24.35,24.35,0,0,0,40.55,224h174.9a24.35,24.35,0,0,0,21.33-12.19A23.51,23.51,0,0,0,236.8,188.09ZM120,104a8,8,0,0,1,16,0v40a8,8,0,0,1-16,0Zm8,88a12,12,0,1,1,12-12A12,12,0,0,1,128,192Z"></path></svg>',

  instances: [],

  parseI18n(form) {
    try {
      return JSON.parse(form.getAttribute("data-i18n") || "{}");
    } catch (_e) {
      return {};
    }
  },

  buildSchema(i18n) {
    return z.object({
      nome: z.string().min(1, i18n.name || "Informe o nome."),
      email: z
        .string()
        .min(1, i18n.email || "Informe o e-mail.")
        .email(i18n.email_invalid || "E-mail inválido."),
      telefone: z.string(),
      vaga: z.string(),
      lgpd_consent: z.enum(["aceito", "nao_aceito"], {
        errorMap: function () {
          return { message: i18n.lgpd || "Selecione uma opção." };
        },
      }),
    });
  },

  initAll() {
    document.querySelectorAll("[data-career-form]").forEach((root) => {
      this.init(root);
    });
  },

  init(root) {
    const form = root.querySelector("[data-career-form-element]");
    if (!form || form.dataset.careerInit === "1") return;
    form.dataset.careerInit = "1";

    const endpoint = form.getAttribute("data-career-endpoint") || "";
    if (!endpoint) return;

    const i18n = this.parseI18n(form);
    const blockId = form.getAttribute("data-career-block-id") || "";
    const instance = {
      root,
      form,
      endpoint,
      i18n,
      schema: this.buildSchema(i18n),
      msgSuccess: form.getAttribute("data-msg-success") || "",
      msgError: form.getAttribute("data-msg-error") || "",
      blockId,
      statusEl: root.querySelector("[data-career-status]"),
      submitBtn: root.querySelector("[data-career-submit]"),
      fileInput: root.querySelector("[data-career-file]"),
      fileNameEl: root.querySelector("[data-career-file-name]"),
      vagaSelect: root.querySelector("[data-career-vaga-select]"),
      attachLabel: i18n.attach || "Anexar",
      resumeError: i18n.resume || "Anexe o currículo.",
    };

    this.attachListeners(instance);
    this.attachVacancyListeners(instance);
    this.enableSubmitButton(instance);
    this.instances.push(instance);
  },

  parseFormData(form) {
    const fd = new FormData(form);
    return {
      nome: String(fd.get("nome") ?? "").trim(),
      email: String(fd.get("email") ?? "").trim(),
      telefone: String(fd.get("telefone") ?? "").trim(),
      vaga: String(fd.get("vaga") ?? "").trim(),
      lgpd_consent: String(fd.get("lgpd_consent") ?? ""),
    };
  },

  validate(instance) {
    const parsed = instance.schema.safeParse(this.parseFormData(instance.form));
    if (!parsed.success) return parsed;

    const file = instance.fileInput?.files?.[0];
    if (!file) {
      return {
        success: false,
        error: {
          flatten: function () {
            return { fieldErrors: { curriculo: [instance.resumeError] } };
          },
          issues: [{ path: ["curriculo"] }],
        },
      };
    }

    return parsed;
  },

  clearFieldErrors(instance) {
    instance.form
      .querySelectorAll(".budget-form__field-error")
      .forEach(function (el) {
        el.textContent = "";
        el.hidden = true;
      });
  },

  clearErrorForField(instance, name) {
    if (!name) return;
    const el = instance.form.querySelector(
      '.budget-form__field-error[data-career-field="' + name + '"]',
    );
    if (el) {
      el.textContent = "";
      el.hidden = true;
    }
  },

  showZodFieldErrors(instance, zodError) {
    const fe = zodError.flatten().fieldErrors;
    Object.keys(fe).forEach(function (key) {
      const messages = fe[key];
      if (!messages || !messages.length) return;
      const errEl = instance.form.querySelector(
        '.budget-form__field-error[data-career-field="' + key + '"]',
      );
      if (errEl) {
        errEl.textContent = messages[0];
        errEl.hidden = false;
      }
    });
  },

  focusFirstInvalidField(instance, zodError) {
    const issue = zodError.issues[0];
    if (!issue || !issue.path || !issue.path.length) return;
    const key = String(issue.path[0]);
    const control =
      instance.form.querySelector('[name="' + key + '"]') ||
      (key === "curriculo" ? instance.fileInput : null) ||
      (key === "lgpd_consent"
        ? instance.form.querySelector('[name="lgpd_consent"]')
        : null);
    if (control && typeof control.focus === "function") {
      control.focus();
    }
  },

  buildPayload(instance) {
    const data = new FormData(instance.form);
    data.append("submitted_at", new Date().toISOString());
    return data;
  },

  showStatus(instance, type) {
    if (!instance.statusEl) return;
    const text = type === "success" ? instance.msgSuccess : instance.msgError;
    instance.statusEl.innerHTML = "";
    instance.statusEl.hidden = false;

    const wrapper = document.createElement("div");
    wrapper.className =
      type === "success"
        ? "form-message alert alert--success h6 blocks-radius"
        : "form-error alert alert--error blocks-radius";
    wrapper.setAttribute("role", type === "success" ? "status" : "alert");
    wrapper.setAttribute("tabindex", "-1");

    const icon = document.createElement("span");
    icon.innerHTML = type === "success" ? this.svgSuccess : this.svgError;
    wrapper.appendChild(icon);
    wrapper.appendChild(document.createTextNode(" " + text));
    instance.statusEl.appendChild(wrapper);
    wrapper.focus();
    instance.statusEl.scrollIntoView({ behavior: "smooth", block: "nearest" });
  },

  async postCareerForm(instance, payload) {
    const res = await fetch(instance.endpoint, {
      method: "POST",
      body: payload,
    });

    let body = {};
    try {
      body = await res.json();
    } catch {
      body = {};
    }

    if (!res.ok) {
      throw new Error(body.message || "Erro " + res.status);
    }

    return body;
  },

  setLoading(instance, isLoading) {
    if (!instance.submitBtn) return;
    const btnText = instance.submitBtn.querySelector(".btn__text");
    const spinner = instance.submitBtn.querySelector(".budget-form__spinner");
    if (btnText) btnText.hidden = isLoading;
    if (spinner) spinner.hidden = !isLoading;
    if (isLoading) {
      instance.submitBtn.disabled = true;
    } else {
      this.enableSubmitButton(instance);
    }
  },

  enableSubmitButton(instance) {
    if (instance.submitBtn) instance.submitBtn.disabled = false;
  },

  onFileChange(instance) {
    if (!instance.fileInput || !instance.fileNameEl) return;
    instance.fileNameEl.textContent =
      instance.fileInput.files.length > 0
        ? instance.fileInput.files[0].name
        : instance.attachLabel;
    this.clearErrorForField(instance, "curriculo");
  },

  onFieldInput(instance, e) {
    const t = e.target;
    if (t && t.name) this.clearErrorForField(instance, t.name);
  },

  onFieldChange(instance, e) {
    const t = e.target;
    if (t && t.name) this.clearErrorForField(instance, t.name);
  },

  async onSubmit(instance, e) {
    e.preventDefault();
    this.clearFieldErrors(instance);

    const result = this.validate(instance);
    if (!result.success) {
      this.showZodFieldErrors(instance, result.error);
      this.focusFirstInvalidField(instance, result.error);
      this.enableSubmitButton(instance);
      return;
    }

    this.setLoading(instance, true);
    if (instance.statusEl) instance.statusEl.hidden = true;

    try {
      await this.postCareerForm(instance, this.buildPayload(instance));
      this.showStatus(instance, "success");
      instance.form.reset();
      if (instance.fileNameEl) instance.fileNameEl.textContent = instance.attachLabel;
      this.clearFieldErrors(instance);
    } catch {
      this.showStatus(instance, "error");
    } finally {
      this.setLoading(instance, false);
    }
  },

  setVagaValue(instance, title) {
    if (!title || !instance.vagaSelect) return;
    instance.vagaSelect.value = title;
    instance.vagaSelect.dispatchEvent(new Event("change", { bubbles: true }));
  },

  attachVacancyListeners(instance) {
    if (!instance.vagaSelect) return;

    instance.root.querySelectorAll("[data-career-vacancy]").forEach((details) => {
      details.addEventListener("toggle", () => {
        if (!details.open) return;
        const title = details.getAttribute("data-vacancy-title");
        this.setVagaValue(instance, title);
      });
    });
  },

  attachListeners(instance) {
    if (instance.fileInput && instance.fileNameEl) {
      instance.fileInput.addEventListener("change", () =>
        this.onFileChange(instance),
      );
    }
    instance.form.addEventListener("input", (e) =>
      this.onFieldInput(instance, e),
    );
    instance.form.addEventListener("change", (e) =>
      this.onFieldChange(instance, e),
    );
    instance.form.addEventListener("submit", (e) =>
      this.onSubmit(instance, e),
    );
  },
};

CareerForm.initAll();

document.addEventListener("shopify:section:load", (evt) => {
  evt.target.querySelectorAll("[data-career-form]").forEach((root) => {
    CareerForm.init(root);
  });
});

export { CareerForm };
